import type { ITaskProjectCustomField } from './customField'
import type { ITask, ITaskEmbeddedUser } from './task'

// Per-project role. Admins manage the project itself (rename/recolour, delete,
// members and their roles); members work its contents — tasks (add/remove/move
// placements + view) and the sections those tasks live in (create/rename/delete/
// reorder). Sections are content, not administration, matching how Asana treats
// them: an Editor can restructure a board without being a project admin.
// UserRole.ADMIN implicitly has admin on every project.
export enum TaskProjectRole {
  Admin = 'admin',
  Member = 'member',
}

export enum TaskProjectStatus {
  NotStarted = 'not_started',
  InProgress = 'in_progress',
  // A standing project that never finishes, e.g. a 1:1 or a DevOps board.
  Ongoing = 'ongoing',
  Blocked = 'blocked',
  Completed = 'completed',
  Archived = 'archived',
}

export const TASK_PROJECT_STATUS_LABELS: Record<TaskProjectStatus, string> = {
  [TaskProjectStatus.NotStarted]: 'Not Started',
  [TaskProjectStatus.InProgress]: 'In Progress',
  [TaskProjectStatus.Ongoing]: 'Ongoing',
  [TaskProjectStatus.Blocked]: 'Blocked',
  [TaskProjectStatus.Completed]: 'Completed',
  [TaskProjectStatus.Archived]: 'Archived',
}

// A closed project drops out of My Tasks and the default Projects list and takes no
// new tasks. Completed and archived behave identically; only the label differs.
export const CLOSED_TASK_PROJECT_STATUSES = [
  TaskProjectStatus.Completed,
  TaskProjectStatus.Archived,
] as const

export const isClosedProjectStatus = (status: string): boolean =>
  CLOSED_TASK_PROJECT_STATUSES.includes(status as (typeof CLOSED_TASK_PROJECT_STATUSES)[number])

export const isProjectClosed = (project: { status: string }): boolean =>
  isClosedProjectStatus(project.status)

export const OPEN_TASK_PROJECT_STATUSES = [
  TaskProjectStatus.NotStarted,
  TaskProjectStatus.InProgress,
  TaskProjectStatus.Ongoing,
  TaskProjectStatus.Blocked,
] as const

// Built-in task fields a project admin can hide or remove. Task name is absent on
// purpose: it is the one field every project keeps.
export enum TaskProjectField {
  Category = 'category',
  Source = 'source',
  Company = 'company',
  Assignee = 'assignee',
  Status = 'status',
  DueDate = 'dueDate',
  CreatedDate = 'createdDate',
  CompletedDate = 'completedDate',
}

// Hidden drops the board column; removed also drops the field from the detail
// panel and the board's bulk actions. No entry means visible.
export enum TaskProjectFieldVisibility {
  Hidden = 'hidden',
  Removed = 'removed',
}

export type ITaskProjectFieldVisibility = Partial<
  Record<TaskProjectField, TaskProjectFieldVisibility>
>

// How a member last viewed the project's tasks; remembered per member.
export enum TaskProjectView {
  List = 'list',
  Calendar = 'calendar',
}

export interface ITaskProjectMember {
  id: string
  projectId: string
  userId: string
  role: TaskProjectRole
  view: TaskProjectView
  user?: ITaskEmbeddedUser | null
  createdAt: string
  updatedAt: string
}

// Ordered grouping of tasks inside a project. Every project has a default
// section (isDefault) that receives new/orphaned placements; it can be renamed
// but not deleted.
export interface ITaskProjectSection {
  id: string
  projectId: string
  name: string
  position: number
  isDefault: boolean
  createdAt: string
  updatedAt: string
}

export interface ITaskProject {
  id: string
  name: string
  // Palette key from OPTION_COLOR_KEYS — the project's swatch colour.
  color: string
  status: TaskProjectStatus
  fieldVisibility: ITaskProjectFieldVisibility
  statusChangedByUserId: string | null
  statusChangedAt: string | null
  // Show payload only: display name of whoever last set the status.
  statusChangedByName?: string | null
  createdByUserId: string | null
  members?: ITaskProjectMember[]
  sections?: ITaskProjectSection[]
  // List payload only: aggregate for the projects overview page.
  taskCount?: number
  // The requesting user's role in this project (list/show payloads).
  currentUserRole?: TaskProjectRole
  // The requesting user's last-chosen view of this project (show payload).
  currentUserView?: TaskProjectView
  // Whether the requesting user pinned this project as a tab (list/show payloads).
  isFavorite?: boolean
  // Whether this project's tasks appear in the requesting user's My Tasks list
  // (list payload). Per-user and on by default — see TaskProjectMyTasksHidden.
  showInMyTasks?: boolean
  // List payload only: null when there is no update yet or the caller isn't a member.
  latestStatus?: ITaskProjectLatestStatus | null
  createdAt: string
  updatedAt: string
}

// A project the requesting user pinned as a tab on the Tasks page. Just enough
// to render the tab strip — the strip mounts on every page in that area, so it
// deliberately avoids the members/sections hydration a full ITaskProject carries.
export interface ITaskProjectFavorite {
  id: string
  name: string
}

// Multi-homing junction: one row per (task, project), each pointing at a
// section within that project. A task in 3 projects has 3 placements.
export interface ITaskProjectPlacement {
  id: string
  taskId: string
  projectId: string
  sectionId: string
  position: number
  // `fieldVisibility` rides only on the detail payload (Show), which loads the full project.
  project?:
    | (Pick<ITaskProject, 'id' | 'name' | 'color' | 'status'> &
        Partial<Pick<ITaskProject, 'fieldVisibility'>>)
    | null
  section?: Pick<ITaskProjectSection, 'id' | 'name' | 'isDefault'> | null
  createdAt: string
  updatedAt: string
}

/** Task row on the per-project list payload: the task + its placement there. */
export type ITaskProjectTask = ITask & {
  placement: Pick<ITaskProjectPlacement, 'id' | 'projectId' | 'sectionId' | 'position'>
}

export interface ITaskProjectSectionWithTasks extends ITaskProjectSection {
  tasks: ITaskProjectTask[]
  // How many tasks in this section match the current filters across the WHOLE
  // result set, not just the loaded page. `tasks` is page-limited (rows paginate
  // globally across sections), so an empty `tasks` array on its own can't tell a
  // section that filtered down to nothing apart from one whose rows simply
  // haven't paged in yet — the list hides only the former.
  matchingTaskCount: number
}

export interface IProjectTasksResponse {
  // Lets the board tell its own payload from the previous board's, which stays
  // in the store until this one's first page lands.
  projectId: string
  sections: ITaskProjectSectionWithTasks[]
  // The project's custom-field columns (ordered), so the List view can render
  // headers even when sections are empty. Per-row values live on each task's
  // `customFields`.
  customFields: ITaskProjectCustomField[]
  fieldVisibility?: ITaskProjectFieldVisibility
  counts: {
    // Total placements matching the current filters — drives the "load more"
    // affordance against how many rows are currently loaded.
    active: number
    // Completed placements currently excluded from `active` because the "Show
    // Completed" chip is off (0 when it's on, or when nothing is hidden).
    completedHidden: number
    // Rows in this view (every page) that carry the unread dot — what "Mark all read" clears.
    unread: number
  }
  pagination: {
    page: number
    perPage: number
  }
}

// Payload of MY_TASKS_CHANGED. `involvedUserIds` (assignee, creator, collaborators)
// lets a page filtered to one person skip changes that can't reach its rows.
export interface IMyTasksChangedEvent {
  originTabId: string | null
  taskIds: string[]
  deletedTaskIds: string[]
  involvedUserIds: string[]
  // Only linked PR data changed. Lists don't show it, so pages refresh just an open panel.
  isPullRequestChange?: boolean
}

// Payload of TASK_PROJECT_CHANGED. A nudge to resync rather than a delta: the
// board is filtered, sorted and paged server-side, so only a refetch can say
// where (or whether) a changed row now belongs.
export interface ITaskProjectChangedEvent {
  projectId: string
  // The browser tab whose request made the change, so that tab can skip its own
  // echo. Null for changes with no tab behind them (Arc, jobs).
  originTabId: string | null
  taskIds: string[]
  deletedTaskIds: string[]
  hasSectionChanges: boolean
  // Name, settings or members changed, so the page re-reads the project itself.
  hasProjectChanges: boolean
  isProjectDeleted: boolean
  // Members just removed. They still get this one event, so their open page can leave.
  removedUserIds: string[]
  // Only linked PR data changed. Lists don't show it, so pages refresh just an open panel.
  isPullRequestChange?: boolean
}

/**
 * The questions Arc's project status summary answers, in the order the panel
 * renders them. Fixed on purpose: a summary asked something different each run
 * reads differently each run, and nobody can trust it.
 */
export enum ProjectStatusQuestion {
  MovedThisWeek = 'movedThisWeek',
  Overdue = 'overdue',
  QuietSections = 'quietSections',
  OpenTaskLoad = 'openTaskLoad',
}

export const PROJECT_STATUS_QUESTION_LABELS: Record<ProjectStatusQuestion, string> = {
  [ProjectStatusQuestion.MovedThisWeek]: 'What moved this week',
  [ProjectStatusQuestion.Overdue]: 'What is overdue, and by how long',
  [ProjectStatusQuestion.QuietSections]: 'Sections with no movement',
  [ProjectStatusQuestion.OpenTaskLoad]: 'Who is carrying the most open tasks',
}

export type IProjectStatusAnswers = Record<ProjectStatusQuestion, string>

export enum ProjectStatusSource {
  Arc = 'arc',
  Manual = 'manual',
}

export const PROJECT_STATUS_HEADLINE_MAX_LENGTH = 200
export const PROJECT_STATUS_ANSWER_MAX_LENGTH = 2000

/** One status update in the project's log, newest first. */
export interface IProjectStatusUpdate {
  id: string
  answers: IProjectStatusAnswers
  headline: string | null
  source: ProjectStatusSource
  // Manual updates only: who wrote it.
  createdByName: string | null
  generatedAt: string
}

export interface IProjectStatusSummaryData {
  // Lets the page tell this project's summary from the previous project's, still in the store.
  projectId: string
  answers: IProjectStatusAnswers | null
  headline: string | null
  source: ProjectStatusSource | null
  createdByName: string | null
  generatedAt: string | null
  /** True when the summary is old enough that it should not read as current. */
  isStale: boolean
  /** The latest updates, newest first; the first is the current status above. */
  history: IProjectStatusUpdate[]
}

// List payload only: the condensed latest status, for projects the caller is a member of.
export interface ITaskProjectLatestStatus {
  headline: string | null
  source: ProjectStatusSource
  generatedAt: string
}
